// Discipline-stratified layout for the Overview Fishbone.
//
// Instead of relying on raw dagre — which spreads 53 stages across a tall
// graph because many stages share a rank — we arrange stages in a
// discipline grid (one column per L1 A-J) with sub-stages stacked
// vertically. Views are placed in a horizontal strip below the grid.
//
// This produces a compact, readable layout that mirrors the spec's
// "backbone + branches" ASCII drawing and fits on a single 1920×720
// canvas at ~1× zoom.

const STAGE_W = 170;
const STAGE_H = 60;
const VIEW_W = 140;
const VIEW_H = 50;
const COL_GAP = 40;
const ROW_GAP = 20;
const VIEW_STRIP_GAP = 80;
const LEFT_PAD = 40;
const TOP_PAD = 30;

// Discipline ordering (matches the catalogue)
const DISCIPLINE_COLS = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];

/** Return a positions dict: id → {x, y}. */
export function computeLayout({ stages, views, includeViews }) {
  const positions = {};
  // Group stages by discipline code
  const groups = {};
  for (const s of stages) {
    const col = s.id.split(".")[0];
    if (!groups[col]) groups[col] = [];
    groups[col].push(s);
  }
  // Sort within each group by code (natural order)
  for (const k of Object.keys(groups)) {
    groups[k].sort((a, b) => compareStageCode(a.id, b.id));
  }

  // Assign column x / row y
  const colX = {};
  DISCIPLINE_COLS.forEach((d, i) => {
    colX[d] = LEFT_PAD + i * (STAGE_W + COL_GAP);
  });

  let maxY = 0;
  for (const d of DISCIPLINE_COLS) {
    const items = groups[d] || [];
    items.forEach((s, idx) => {
      const x = colX[d];
      const y = TOP_PAD + idx * (STAGE_H + ROW_GAP);
      positions[s.id] = { x, y };
      if (y > maxY) maxY = y;
    });
  }

  // Views strip below the tallest stage column
  if (includeViews) {
    const stripTop = maxY + STAGE_H + VIEW_STRIP_GAP;
    const stripWidth = DISCIPLINE_COLS.length * (STAGE_W + COL_GAP);
    const perRow = 6;
    views.forEach((v, i) => {
      const col = i % perRow;
      const row = Math.floor(i / perRow);
      const colSpan = stripWidth / perRow;
      positions[v.node_id] = {
        x: LEFT_PAD + col * colSpan + (colSpan - VIEW_W) / 2,
        y: stripTop + row * (VIEW_H + 14),
      };
    });
  }

  return positions;
}

function compareStageCode(a, b) {
  // "A.1" vs "A.10" — split on "." and compare numerically per segment
  const sa = a.split(".");
  const sb = b.split(".");
  const len = Math.max(sa.length, sb.length);
  for (let i = 0; i < len; i++) {
    const pa = sa[i] ?? "";
    const pb = sb[i] ?? "";
    const na = Number(pa);
    const nb = Number(pb);
    if (!isNaN(na) && !isNaN(nb)) {
      if (na !== nb) return na - nb;
    } else if (pa !== pb) {
      return pa < pb ? -1 : 1;
    }
  }
  return 0;
}

export const NODE_SIZES = { STAGE_W, STAGE_H, VIEW_W, VIEW_H };
